// V11.18 — Injection dynamique des balises Open Graph pour partage riche
// sur Facebook / WhatsApp / X / LinkedIn.
//
// Attention : dans une SPA, ces balises ne sont visibles que par les
// clients qui exécutent JavaScript (X/Discord modernes, LinkedIn parfois).
// Les crawlers "old-school" (Facebook debug par défaut) lisent le HTML
// initial → l'index.html doit contenir une balise OG de secours (fait).
//
// Ce helper permet cependant à des outils modernes (aperçu iOS/macOS,
// Slack unfurl, moteurs de scraping headless) d'afficher la BONNE image
// contextuelle (portrait athlète, cover club, etc.).
//
// Usage :
//   useOgTags({
//     title, description, image, url,
//   });

import { useEffect } from "react";

const OG_ORIGIN = (typeof window !== "undefined" ? window.location.origin : "");
const FALLBACK_IMAGE = `${OG_ORIGIN}/branding/og-share.png`;

function ensureMeta(selector, attrName, attrValue) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    if (selector.startsWith("meta[property=")) {
      el.setAttribute("property", attrName);
    } else {
      el.setAttribute("name", attrName);
    }
    document.head.appendChild(el);
  }
  el.setAttribute("content", attrValue || "");
}

/**
 * @param {{title?:string, description?:string, image?:string, url?:string, type?:string}} tags
 */
export function useOgTags(tags) {
  useEffect(() => {
    if (!tags) return;
    const title = tags.title || "AITO.PF — La mémoire vivante du sport polynésien";
    const description = tags.description || "Athlètes, coachs, clubs et fédérations de Polynésie française.";
    const image = tags.image ? _absoluteUrl(tags.image) : FALLBACK_IMAGE;
    const url = tags.url ? _absoluteUrl(tags.url) : (typeof window !== "undefined" ? window.location.href : OG_ORIGIN);
    const type = tags.type || "website";

    // Titre HTML (utile pour les onglets aussi)
    const prevTitle = document.title;
    document.title = title;

    // Open Graph
    ensureMeta('meta[property="og:title"]', "og:title", title);
    ensureMeta('meta[property="og:description"]', "og:description", description);
    ensureMeta('meta[property="og:image"]', "og:image", image);
    ensureMeta('meta[property="og:image:secure_url"]', "og:image:secure_url", image);
    ensureMeta('meta[property="og:url"]', "og:url", url);
    ensureMeta('meta[property="og:type"]', "og:type", type);
    // Twitter Cards
    ensureMeta('meta[name="twitter:title"]', "twitter:title", title);
    ensureMeta('meta[name="twitter:description"]', "twitter:description", description);
    ensureMeta('meta[name="twitter:image"]', "twitter:image", image);
    ensureMeta('meta[name="twitter:card"]', "twitter:card", "summary_large_image");
    // V13.25 — noindex conditionnel (mineurs, opposition, fiches masquées)
    if (tags.noindex) {
      ensureMeta('meta[name="robots"]', "robots", "noindex, nofollow");
    } else {
      // Nettoyage : si une page précédente avait posé noindex, on remet index
      const existing = document.head.querySelector('meta[name="robots"]');
      if (existing) existing.setAttribute("content", "index, follow");
    }

    return () => {
      document.title = prevTitle;
    };
  }, [tags?.title, tags?.description, tags?.image, tags?.url, tags?.type]); // eslint-disable-line react-hooks/exhaustive-deps
}

function _absoluteUrl(u) {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return OG_ORIGIN + (u.startsWith("/") ? u : "/" + u);
}
